import { OrderType } from '@modules/common/models/enums'
import { CancellableRequest } from '@services/HttpService'
import { stores, nav } from '@stores'
import { action, computed, observable } from 'mobx'
import { OrderService } from '../../services/orderService'
import map from 'lodash/map'
import find from 'lodash/find'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { getEnumValue } from '@utils/get-enum-value'
import { orderKPIGlobalTopBarStore } from '../OrderKPIGlobalTopBarStore'
import { computedFn } from 'mobx-utils'
import { getUTCFromTimeStamp } from '@services/commonServices'
import { cloneDeep } from 'lodash'
import {
  OrderTemplatePreferences,
  orderTemplatePreferencesStore
} from './OrderTemplatePreferencesStore'
import { METRIC_ATTRIBUTES_OTB } from '@modules/order/constants/uiConstants'

/**
 * @todo: - 1. Add logic to fetch view configurations
 *          2. Add logic to store summary
 */

interface IClientOrder {
  name: string
  id: string
  doorOrders?: any
  buyingSession?: any
  deleted?: boolean
}

export interface OrderKPIs {
  totalQty: number
  totalValue: number
}

export interface OrderKPIByTypes {
  draft: OrderKPIs
  sized: OrderKPIs
}

export interface ISize {
  id: string
  name: string
  sizeCode: number
  isInactive?: boolean
}

export class OrderDataStore {
  private orderService = new OrderService()
  @observable summary: any = {}
  @observable summaryLoading: boolean
  @observable summaryLoaded: boolean = false
  @observable clientOrdersMap = {}
  @observable clientOrders: IClientOrder[] = []
  @observable isOrderNavigationOpen: boolean = false
  @observable isNavDataLoading: boolean
  @observable summaryDataUpdating: boolean = false
  @observable doorsUpdating: boolean = false
  @observable selectedDoors: any = []
  @observable otbData: any = {}
  @observable tableSetting: any = {}
  @observable loadedKPIAggregateData: boolean = false

  fetchProductsFromAPI(
    activity: string,
    family: string,
    line: string,
    fromIndex: number,
    toIndex: number
  ): CancellableRequest<any> | Promise<any[]> {
    throw new Error('Method not implemented.')
  }
  fetchHierarchyFromAPI(): CancellableRequest<any> | Promise<any> {
    throw new Error('Method not implemented.')
  }

  removeProductAPI(productId: string): Promise<any> {
    throw new Error('Method not implemented.')
  }

  @computed get isEnhancedViewEnabled() {
    const {
      productConfiguration: { enhancedViewEnabled }
    } = stores
    return enhancedViewEnabled
  }

  @action
  initialize = async () => {
    const {
      nav: {
        queryParams: { clientType, orderId, orderType }
      }
    } = stores
    this.fetchSummary()
    if (clientType === getEnumValue(VIPClientType.Internal)) {
      if (this.isEnhancedViewEnabled) {
        this.getEnhancedOTBData().then(otb => {
          orderKPIGlobalTopBarStore.setEnhancedOTBData(otb?.data)
        })
      } else {
        if (orderId) {
          this.otbData = await this.orderService.getOTBData(orderId, orderType)
          orderKPIGlobalTopBarStore.setOrderKPIsForGlobalTopBar({ otbData: this.otbData })
        }
      }
    }
  }

  @action getEnhancedOTBData = () => {
    const {
      nav: {
        queryParams: { buyingSession, buyingSessionGroupId, clientId }
      }
    } = stores

    const body = {
      groupBy: [
        {
          attributeId: 'buyer',
          orderBy: 'A_TO_Z'
        },
        {
          attributeId: 'location',
          orderBy: 'A_TO_Z'
        }
      ],
      metricAttributes: METRIC_ATTRIBUTES_OTB,
      buyingSessionId: buyingSession,
      useExchangeRateForBuyingSessionGroupId: buyingSessionGroupId,
      usePlanningBudgetsWithNoBuyer: false
    }
    return this.orderService.getEnhancedOTBData(buyingSessionGroupId, clientId, body)
  }

  @action
  fetchSummary = async () => {
    try {
      this.summaryLoading = true
      this.summary = await this.getSummaryData()
      orderKPIGlobalTopBarStore.setOrderKPIsForGlobalTopBar({ summary: this.summary })
    } catch (e) {
      console.error(e)
    } finally {
      this.summaryLoading = false
      this.summaryLoaded = true
    }
  }

  @action getSummaryData = () => {
    try {
      const {
        nav: {
          queryParams: { orderId, orderType }
        }
      } = stores
      if (orderId) {
        return orderType === OrderType.Door
          ? this.orderService.getDoorOrderSummary(orderId).then(res => res)
          : this.orderService.getClientOrderSummary(orderId).then(res => res)
      }
    } catch (e) {
      console.error(e)
    }
  }

  @action
  fetchClientOrderNames = async () => {
    this.isNavDataLoading = true
    try {
      const {
        nav: {
          queryParams: { buyingSessionGroupId, clientId }
        }
      } = stores
      const clientOrders = await this.orderService.getClientOrderNames(
        buyingSessionGroupId,
        clientId
      )
      this.clientOrders = clientOrders
    } finally {
      this.isNavDataLoading = false
    }
  }

  @action
  getTableSetting = async () => {
    try {
      const {
        nav: {
          queryParams: { orderId, orderType }
        }
      } = stores

      if (!orderId) return

      const response = await this.orderService.getTableSetting(orderId, orderType)
      const tempResp = cloneDeep(response || {})
      if (orderType === OrderType.Client) {
        // Setting the order templates Data from this table settings API call
        const newData = tempResp?.[OrderTemplatePreferences.TEMPLATES_PREFERENCE] || {}
        orderTemplatePreferencesStore.setTemplates(newData)
      }
      if (tempResp?.[OrderTemplatePreferences.TEMPLATES_PREFERENCE]) {
        delete tempResp[OrderTemplatePreferences.TEMPLATES_PREFERENCE]
      }
      this.tableSetting = { ...tempResp }
    } catch (e) {
      console.log('error in getting table setting', e)
    }
  }

  @action
  updatedTableSetting = async object => {
    try {
      const {
        nav: {
          queryParams: { orderId, orderType }
        }
      } = stores

      const newPaylod = cloneDeep({
        ...this.tableSetting,
        ...object
      })
      // removing templates to avoid them from overriding in table settings change
      delete newPaylod[OrderTemplatePreferences.TEMPLATES_PREFERENCE]

      this.tableSetting = {
        ...this.tableSetting,
        ...object
      }
      await this.orderService.updateTableSetting(orderId, newPaylod, orderType)
    } catch (e) {
      console.log('error in updating table setting', e)
    }
  }

  @action
  setClientOrderOpenState = id => {
    if (!this.clientOrdersMap[id]) {
      this.clientOrdersMap[id] = true
    } else {
      this.clientOrdersMap[id] = !this.clientOrdersMap[id]
    }
  }

  @computed get isClientMaster() {
    return Boolean(this.summary?.client?.isClientMaster)
  }

  // function to update poRef,Notes and Delivery Date.
  @action
  updateSummary = async summary => {
    const {
      nav: {
        queryParams: { orderType }
      }
    } = stores
    this.summaryDataUpdating = true
    try {
      const response = await this.orderService.updateSummaryData(
        this.summary.id,
        summary,
        orderType
      )
      if (response?.data?.updatedOrderDetails.message === 'Success') {
        if (summary?.deliveryDate) {
          summary.deliveryDate = getUTCFromTimeStamp(
            summary.deliveryDate * 1000,
            'YYYY-MM-DDTHH:mm:ss.SSSZ'
          )
        }
        this.updatedOrderDetails(summary)
      }
    } catch (e) {
    } finally {
      this.summaryDataUpdating = false
    }
  }

  @action
  updatedOrderDetails = summary => {
    this.summary = {
      ...this.summary,
      ...summary
    }

    orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes(summary)
  }

  @action
  setSelectedDoors = doors => {
    this.selectedDoors = doors
  }

  @action
  addRemoveDoors = async (addedDoors, removedDoors, restoredDoors) => {
    const {
      nav: {
        queryParams: { buyingSessionGroupId, clientId, orderId }
      }
    } = stores
    this.doorsUpdating = true
    try {
      try {
        if (addedDoors?.length) {
          const res = await this.orderService.addRemoveDoors(
            clientId,
            buyingSessionGroupId,
            orderId,
            addedDoors,
            null,
            null
          )
          this.summary.doorOrders = [
            ...this.summary.doorOrders,
            ...addedDoors.map((door, index) => ({
              door,
              id: res.doorOrderIds[index],
              deleted: false
            }))
          ]
          this.summary = { ...this.summary }
        }
      } catch (e) {}
      try {
        if (removedDoors?.length) {
          const { clientOrder } = await this.orderService.addRemoveDoors(
            clientId,
            buyingSessionGroupId,
            orderId,
            null,
            map(removedDoors, 'id'),
            null
          )
          this.summary.doorOrders = map(this.summary?.doorOrders, doorOrder => {
            const deletedOrder = removedDoors?.find(door => door.id === doorOrder?.id)
            return deletedOrder ? { ...deletedOrder, deleted: true } : { ...doorOrder }
          })
          this.summary = { ...this.summary, clientOrder }
          orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({ clientOrder })
        }
      } catch (e) {}
      try {
        if (restoredDoors?.length) {
          const { clientOrder } = await this.orderService.addRemoveDoors(
            clientId,
            buyingSessionGroupId,
            orderId,
            null,
            null,
            map(restoredDoors, 'id')
          )
          this.summary.doorOrders = map(this.summary?.doorOrders, doorOrder => {
            const restoredOrder = restoredDoors?.find(door => door.id === doorOrder?.id)
            return restoredOrder ? { ...restoredOrder, deleted: false } : { ...doorOrder }
          })
          this.summary = { ...this.summary, clientOrder }
          orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({ clientOrder })
        }
      } catch (e) {}
    } finally {
      this.doorsUpdating = false
      if (this.clientOrders?.length) {
        const clientOrder = find(this.clientOrders, ['id', orderId])
        if (clientOrder) {
          clientOrder.doorOrders = this.summary.doorOrders
        }
      }
    }
  }

  getSummaryDoorOrder = computedFn((doorOrderId: string): any => {
    const {
      queryParams: { orderType }
    } = nav
    return (
      (orderType === OrderType.Door
        ? this.summary
        : this.summary?.doorOrders?.find(doorOrder => doorOrder?.id === doorOrderId)) ||
      {}
    )
  })

  @action
  destroy() {
    this.summary = {}
    this.tableSetting = {}
    this.summaryLoading = false
    this.isOrderNavigationOpen = false
    this.summaryDataUpdating = false
    this.doorsUpdating = false
    this.selectedDoors = []
    this.otbData = {}
    this.loadedKPIAggregateData = false
  }
}
